import {Injectable, NgZone} from '@angular/core';
import {HttpClient, HttpContext, HttpHeaders, HttpParams} from '@angular/common/http';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {saveAs} from 'file-saver';
import {EventSourcePolyfill} from 'event-source-polyfill';
import {environment} from '../../../environments/environment';
import {Token} from '../../api/session/token';
import {SIN_INDICADOR_CARGA} from '../../shell/loading/loading.interceptor';
import {
    AccesoMensajesCliente, AdjuntoConversacion, ComunicadoResumen, ConversacionHilo, ConversacionResumen, EntidadConversacion,
    FiltroConversaciones, FiltroGrupo, GruposConversacion, IndicadorConversacion, NuevaConversacion, NuevoComunicado, ResumenConversaciones,
    TipoEntidadConversacion
} from './conversaciones.model';

/**
 * Conversaciones con CNC. HttpClient directo (no ApiService ni BEI): hace falta multipart con
 * varios ficheros, blobs y peticiones de fondo sin el «CARGANDO». Los errores los enseña quien
 * llama (ErrorDisplayService). ESTE FICHERO ES IDÉNTICO EN cnc-client Y cnc-admin.
 *
 * El contador se refresca al arrancar, al navegar (lo pide la cabecera), cada 3 minutos y cuando
 * llega un aviso por Mercure. Mercure es un extra: si el hub no responde, el sondeo basta.
 */
@Injectable({providedIn: 'root'})
export class ConversacionesService {
    static readonly SONDEO_MS = 3 * 60 * 1000;

    readonly resumen$ = new BehaviorSubject<ResumenConversaciones>({});
    /** Algo ha cambiado (acción propia o aviso de Mercure): las listas y el hilo abiertos recargan. */
    readonly cambios$ = new Subject<void>();

    private _sondeo: any = null;
    private _avisos: any = null;

    constructor(private _http: HttpClient, private _zone: NgZone) {
    }

    static paramsFiltro(params: HttpParams, filtro: FiltroConversaciones, grupo: FiltroGrupo = null): HttpParams {
        if (grupo === 'sin') {
            params = params.set('exists[grupo]', 'false');
        } else if (grupo !== null) {
            params = params.set('grupo', String(grupo));
        }
        switch (filtro) {
            case 'no-leidas':
                return params.set('noLeida', 'true');
            case 'pendientes':
                return params.set('pendienteRespuesta', 'true');
            case 'abiertas':
                return params.append('estado[]', 'ABIERTA').append('estado[]', 'PROPUESTA_CIERRE');
            case 'propuestas':
                return params.set('estado', 'PROPUESTA_CIERRE');
            case 'cerradas':
                return params.set('estado', 'CERRADA');
            default:
                return params;
        }
    }

    static urlAvisos(hub: string, api: string, destinos: string[]): string {
        return hub + '?' + destinos.map(d => 'topic=' + encodeURIComponent(`${api}/conversaciones/avisos/${d}`)).join('&');
    }

    /**
     * `silencioso` (también en deEntidad() e hilo()): sin el «CARGANDO». Solo para las recargas que
     * dispara cambios$; la carga inicial y lo que pide el usuario sí lo muestran.
     */
    listar(filtro: FiltroConversaciones, pagina = 1, busqueda = '', grupo: FiltroGrupo = null,
           porPagina = 30, silencioso = false): Observable<{ conversaciones: ConversacionResumen[], total: number }> {
        let params = new HttpParams().set('page', String(pagina)).set('itemsPerPage', String(porPagina));
        params = ConversacionesService.paramsFiltro(params, filtro, grupo);
        if (busqueda.trim()) {
            params = params.set('general-search', busqueda.trim());
        }
        return this._http.get<any>(`${this._url}/conversacions`, {headers: this._cabeceras(false), params, context: this._contexto(silencioso)}).pipe(
            map(r => ({conversaciones: r['hydra:member'] ?? [], total: r['hydra:totalItems'] ?? 0})));
    }

    deEntidad(entidad: EntidadConversacion, silencioso = false): Observable<ConversacionResumen[]> {
        const params = new HttpParams().set(entidad.tipo, String(entidad.id)).set('itemsPerPage', '50');
        return this._http.get<any>(`${this._url}/conversacions`, {headers: this._cabeceras(false), params, context: this._contexto(silencioso)}).pipe(
            map(r => r['hydra:member'] ?? []));
    }

    hilo(id: number, silencioso = false): Observable<ConversacionHilo> {
        return this._http.get<ConversacionHilo>(`${this._url}/conversacions/${id}`, {headers: this._cabeceras(false), context: this._contexto(silencioso)});
    }

    crear(datos: NuevaConversacion, archivos: File[]): Observable<ConversacionHilo> {
        const formulario = new FormData();
        formulario.append('asunto', datos.asunto);
        formulario.append('texto', datos.texto);
        if (datos.entidad) {
            formulario.append(datos.entidad.tipo, String(datos.entidad.id));
        }
        if (datos.clienteId) {
            formulario.append('cliente', String(datos.clienteId));
        }
        if (datos.grupoId) {
            formulario.append('grupo', String(datos.grupoId));
        }
        archivos.forEach(a => formulario.append('files[]', a, a.name));
        return this._http.post<ConversacionHilo>(`${this._url}/conversacions`, formulario, {headers: this._cabeceras(false)})
            .pipe(tap(() => this._cambio()));
    }

    responder(id: number, texto: string, archivos: File[]): Observable<ConversacionHilo> {
        const formulario = new FormData();
        formulario.append('texto', texto);
        archivos.forEach(a => formulario.append('files[]', a, a.name));
        return this._http.post<ConversacionHilo>(`${this._url}/conversacions/${id}/mensajes`, formulario, {headers: this._cabeceras(false)})
            .pipe(tap(() => this._cambio()));
    }

    /** No emite `cambios$`: el hilo que lo pide ya está al día y recargarlo sería un bucle. */
    marcarLeida(id: number): Observable<ConversacionHilo> {
        return this._patch(id, 'leida', {}, true).pipe(tap(() => this.refrescarResumen()));
    }

    proponerCierre(id: number, texto: string | null): Observable<ConversacionHilo> {
        return this._patch(id, 'proponer-cierre', {texto}).pipe(tap(() => this._cambio()));
    }

    rechazarCierre(id: number, texto: string): Observable<ConversacionHilo> {
        return this._patch(id, 'rechazar-cierre', {texto}).pipe(tap(() => this._cambio()));
    }

    cerrar(id: number, texto: string | null): Observable<ConversacionHilo> {
        return this._patch(id, 'cerrar', {texto}).pipe(tap(() => this._cambio()));
    }

    pasarAGrupo(id: number, grupoId: number): Observable<ConversacionHilo> {
        return this._patch(id, 'grupo', {grupo: grupoId}).pipe(tap(() => this._cambio()));
    }

    /** Grupos que atienden mensajes. A CNC le llegan además `esAdmin` y `mio` en cada grupo. */
    grupos(): Observable<GruposConversacion> {
        return this._http.get<GruposConversacion>(`${this._url}/conversaciones/grupos`, {headers: this._cabeceras(false), context: this._silencio()});
    }

    indicadores(tipo: TipoEntidadConversacion): Observable<Record<string, IndicadorConversacion>> {
        return this._http.get<Record<string, IndicadorConversacion>>(`${this._url}/conversaciones/indicadores`, {
            headers: this._cabeceras(false), params: new HttpParams().set('tipo', tipo), context: this._silencio()
        });
    }

    blobAdjunto(id: number): Observable<Blob> {
        return this._http.get(`${this._url}/conversaciones/adjuntos/${id}`, {headers: this._cabeceras(false), responseType: 'blob'});
    }

    descargarAdjunto(adjunto: AdjuntoConversacion): Observable<void> {
        return this.blobAdjunto(adjunto.id).pipe(map(blob => saveAs(blob, adjunto.nombreFicheroOriginal)));
    }

    refrescarResumen(): void {
        if (!Token.fromLocalStorage().isValid()) {
            return;
        }
        this._http.get<ResumenConversaciones>(`${this._url}/conversaciones/resumen`, {headers: this._cabeceras(false), context: this._silencio()})
            .subscribe({next: r => this.resumen$.next(r || {}), error: () => null});
    }

    iniciarSondeo(): void {
        if (this._sondeo) {
            return;
        }
        this.refrescarResumen();
        this._zone.runOutsideAngular(() => {
            this._sondeo = setInterval(() => {
                if (!document.hidden) {
                    this._zone.run(() => this.refrescarResumen());
                }
            }, ConversacionesService.SONDEO_MS);
        });
    }

    detenerSondeo(): void {
        clearInterval(this._sondeo);
        this._sondeo = null;
        this.desconectarAvisos();
    }

    /**
     * destinos: ['cliente-<id>'] en cnc-client; en cnc-admin, los que da la API en grupos().avisos ('cnc', 'grupo-<id>'…, 'comercial-<id>').
     * Sin destinos no conecta. El aviso no trae datos: solo dispara la recarga.
     */
    conectarAvisos(destinos: string[], mercureToken: string | null): void {
        this.desconectarAvisos();
        if (destinos.length === 0 || !mercureToken || !environment.mercureHub) {
            return;
        }
        try {
            const url = ConversacionesService.urlAvisos(environment.mercureHub, this._url, destinos);
            this._zone.runOutsideAngular(() => {
                this._avisos = new EventSourcePolyfill(url, {
                    headers: {Authorization: 'Bearer ' + mercureToken},
                    heartbeatTimeout: 86400000,
                });
                this._avisos.addEventListener('message', () => this._zone.run(() => this._cambio()));
                this._avisos.addEventListener('error', (e: any) => {
                    if (e?.target?.readyState === 0) {
                        this.desconectarAvisos();
                    }
                });
            });
        } catch (e) {
            this._avisos = null;
        }
    }

    desconectarAvisos(): void {
        this._avisos?.close();
        this._avisos = null;
    }

    /** Casilla «Mensajes activados» de la ficha del cliente: solo cnc-admin (la API exige empleado del grupo Mensajes). */
    accesoCliente(clienteId: number): Observable<AccesoMensajesCliente> {
        return this._http.get<AccesoMensajesCliente>(`${this._url}/conversaciones/clientes/${clienteId}/acceso`,
            {headers: this._cabeceras(false), context: this._silencio()});
    }

    cambiarAccesoCliente(clienteId: number, activos: boolean): Observable<AccesoMensajesCliente> {
        return this._http.patch<AccesoMensajesCliente>(`${this._url}/conversaciones/clientes/${clienteId}/acceso`, {activos},
            {headers: this._cabeceras()});
    }

    /** Comunicados: avisos de CNC a varios clientes a la vez (no son conversaciones). `silencioso`: igual que en listar()/hilo(). */
    comunicados(silencioso = false): Observable<ComunicadoResumen[]> {
        return this._http.get<ComunicadoResumen[]>(`${this._url}/conversaciones/comunicados`,
            {headers: this._cabeceras(false), context: this._contexto(silencioso)});
    }

    comunicado(id: number): Observable<ComunicadoResumen> {
        return this._http.get<ComunicadoResumen>(`${this._url}/conversaciones/comunicados/${id}`, {headers: this._cabeceras(false)});
    }

    crearComunicado(datos: NuevoComunicado, archivos: File[]): Observable<ComunicadoResumen> {
        const formulario = new FormData();
        formulario.append('asunto', datos.asunto);
        formulario.append('texto', datos.texto);
        archivos.forEach(a => formulario.append('files[]', a, a.name));
        return this._http.post<ComunicadoResumen>(`${this._url}/conversaciones/comunicados`, formulario, {headers: this._cabeceras(false)})
            .pipe(tap(() => this._cambio()));
    }

    /** Cuántos clientes recibirían el comunicado con los criterios actuales (sin selector todavía: siempre vacíos). */
    destinatariosPrevistos(): Observable<number> {
        return this._http.post<{ total: number }>(`${this._url}/conversaciones/comunicados/destinatarios-previstos`, {criterios: {}},
            {headers: this._cabeceras()}).pipe(map(r => r.total));
    }

    retirarComunicado(id: number): Observable<ComunicadoResumen> {
        return this._http.patch<ComunicadoResumen>(`${this._url}/conversaciones/comunicados/${id}/retirar`, {}, {headers: this._cabeceras()})
            .pipe(tap(() => this._cambio()));
    }

    /** No emite `cambios$`: igual que marcarLeida(), solo refresca el resumen (el «Entendido» del diálogo y abrir uno). */
    marcarComunicadosLeidos(ids: number[]): Observable<void> {
        return this._http.patch<void>(`${this._url}/conversaciones/comunicados/leidos`, {ids}, {headers: this._cabeceras()})
            .pipe(tap(() => this.refrescarResumen()));
    }

    blobAdjuntoComunicado(id: number): Observable<Blob> {
        return this._http.get(`${this._url}/conversaciones/comunicados/adjuntos/${id}`, {headers: this._cabeceras(false), responseType: 'blob'});
    }

    descargarAdjuntoComunicado(adjunto: AdjuntoConversacion): Observable<void> {
        return this.blobAdjuntoComunicado(adjunto.id).pipe(map(blob => saveAs(blob, adjunto.nombreFicheroOriginal)));
    }

    private get _url(): string {
        return environment.apiUrl;
    }

    /** El token se lee en cada llamada: capturarlo una vez lo deja caducado tras volver a entrar. */
    private _cabeceras(json = true): HttpHeaders {
        let cabeceras = new HttpHeaders({
            Accept: 'application/ld+json, application/json',
            Authorization: 'Bearer ' + Token.fromLocalStorage().token
        });
        if (json) {
            cabeceras = cabeceras.set('Content-Type', 'application/ld+json');
        }
        return cabeceras;
    }

    private _silencio(): HttpContext {
        return new HttpContext().set(SIN_INDICADOR_CARGA, true);
    }

    private _contexto(silencioso: boolean): HttpContext {
        return silencioso ? this._silencio() : new HttpContext();
    }

    private _patch(id: number, accion: string, cuerpo: any, silencioso = false): Observable<ConversacionHilo> {
        return this._http.patch<ConversacionHilo>(`${this._url}/conversacions/${id}/${accion}`, cuerpo, {
            headers: this._cabeceras(), context: this._contexto(silencioso)
        });
    }

    private _cambio(): void {
        this.refrescarResumen();
        this.cambios$.next();
    }
}
